/**
 * HostPagePusher — pushes host page content when the widget panel is open.
 *
 * Uses document.body margin to shift content:
 *   - marginRight  for vertical (right sidebar) mode
 *   - marginBottom for immersive (bottom panel) mode
 *
 * Also detects position:fixed / position:sticky elements (navbars, footers)
 * and adjusts them so they don't overlap the panel.
 *
 * Transitions are enabled for open/close but disabled during drag resize
 * to avoid layout jank.
 */

type PushMode = 'vertical' | 'immersive';

interface FixedElState {
  right: string;
  width: string;
  transition: string;
}

/**
 * Mobile-viewport threshold below which we never push host content.
 *
 * Must match the immersive-panel mobile breakpoint in
 * `styles/views/_immersive.scss` (currently 600px). When the panel CSS
 * switches into mobile-overlay sizing (40vh top / 60vh height) the pusher
 * must also switch off, otherwise the host body still gets a marginBottom
 * applied and the page above the panel gets squished.
 *
 * Also avoids a second class of bug on small screens: mutating
 * document.body margins causes host theme components — particularly
 * carousels/slideshows that re-measure on body resize — to land on
 * transient zero-width slides during drag (Samsung S23 + Shopify Horizon
 * announcement-bar slideshow).
 */
const MOBILE_OVERLAY_THRESHOLD_PX = 600;

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth <= MOBILE_OVERLAY_THRESHOLD_PX;
}

export class HostPagePusher {
  private activeMode: PushMode | null = null;
  private currentPx = 0;
  private originalMarginRight = '';
  private originalMarginBottom = '';
  private originalTransition = '';
  private animating = true;

  /** Widget host element to exclude from fixed-element scanning. */
  private widgetHost: Element | null = null;

  /** Tracked fixed/sticky elements and their original inline styles. */
  private fixedEls: Map<HTMLElement, FixedElState> = new Map();

  constructor(widgetHost?: Element) {
    const s = document.body.style;
    this.originalMarginRight = s.marginRight;
    this.originalMarginBottom = s.marginBottom;
    this.originalTransition = s.transition;
    this.widgetHost = widgetHost ?? null;
  }

  /** Push page content left by `widthPx` (vertical sidebar). */
  pushRight(widthPx: number): void {
    if (isMobileViewport()) return;
    this.activeMode = 'vertical';
    this.currentPx = widthPx;
    this.animating = true;
    this.collectFixedElements();
    this.apply();
  }

  /** Push page content up by `heightPx` (immersive bottom panel). */
  pushBottom(heightPx: number): void {
    if (isMobileViewport()) return;
    this.activeMode = 'immersive';
    this.currentPx = heightPx;
    this.animating = true;
    this.collectFixedElements();
    this.apply();
  }

  /** Update margin during drag — synchronous, no transition. */
  updateDuringDrag(valuePx: number): void {
    if (!this.activeMode) return;
    if (isMobileViewport()) return;
    this.animating = false;
    this.currentPx = valuePx;
    this.apply();
  }

  /** Re-enable transition after drag ends. */
  endDrag(): void {
    if (!this.activeMode) return;
    if (isMobileViewport()) return;
    this.animating = true;
    this.apply();
  }

  /** Restore body margins and fixed elements to their original values. */
  reset(): void {
    const s = document.body.style;
    s.marginRight = this.originalMarginRight;
    s.marginBottom = this.originalMarginBottom;
    s.transition = this.originalTransition;
    this.restoreFixedElements();
    this.activeMode = null;
    this.currentPx = 0;
  }

  // ---------------------------------------------------------------------------
  // Fixed / sticky element detection
  // ---------------------------------------------------------------------------

  /**
   * Scan the page for position:fixed or position:sticky elements that span
   * most of the viewport width. Save their original inline styles so we can
   * adjust and later restore them.
   *
   * Only called on open / mode-switch (not during drag) so the DOM scan is
   * acceptable perf-wise.
   */
  private collectFixedElements(): void {
    this.restoreFixedElements();

    // Only vertical mode needs navbar adjustment — immersive (bottom panel)
    // doesn't overlap top-anchored navbars.
    if (this.activeMode !== 'vertical') return;

    const all = document.querySelectorAll('*');
    for (let i = 0; i < all.length; i++) {
      const el = all[i] as HTMLElement;

      // Skip our own widget
      if (this.widgetHost && (el === this.widgetHost || this.widgetHost.contains(el))) continue;

      const cs = getComputedStyle(el);

      // Only adjust position:fixed elements.
      // Sticky elements flow within their parent and already respect the body
      // marginRight we apply — adjusting them too causes a double push.
      if (cs.position !== 'fixed') continue;

      const rect = el.getBoundingClientRect();
      // Only adjust elements that span most of the viewport width (>80%)
      if (rect.width <= window.innerWidth * 0.8) continue;

      this.fixedEls.set(el, {
        right: el.style.right,
        width: el.style.width,
        transition: el.style.transition,
      });
    }
  }

  /** Restore all tracked fixed elements to their original inline styles. */
  private restoreFixedElements(): void {
    for (const [el, orig] of this.fixedEls) {
      el.style.right = orig.right;
      el.style.width = orig.width;
      el.style.transition = orig.transition;
    }
    this.fixedEls.clear();
  }

  // ---------------------------------------------------------------------------
  // Apply
  // ---------------------------------------------------------------------------

  private apply(): void {
    const s = document.body.style;
    const transition = this.animating ? 'margin 0.3s ease' : 'none';
    s.transition = transition;

    if (this.activeMode === 'vertical') {
      s.marginRight = `${this.currentPx}px`;
      s.marginBottom = this.originalMarginBottom;
    } else if (this.activeMode === 'immersive') {
      s.marginBottom = `${this.currentPx}px`;
      s.marginRight = this.originalMarginRight;
    }

    this.applyToFixedElements();
  }

  /**
   * Adjust tracked fixed elements for vertical mode.
   *
   * Strategy: set `right` to the panel width so elements sized via
   * left+right shrink automatically. For elements with explicit `width`
   * (e.g. `width:100%`), also override width so they don't extend behind
   * the panel.
   */
  private applyToFixedElements(): void {
    if (this.activeMode !== 'vertical') return;

    const elTransition = this.animating
      ? 'right 0.3s ease, width 0.3s ease'
      : 'none';

    for (const [el, orig] of this.fixedEls) {
      el.style.transition = elTransition;
      el.style.right = `${this.currentPx}px`;

      // If the element had an explicit inline width (100%, 100vw, etc.)
      // or no explicit width but relies on width:100% from a stylesheet,
      // override it so it doesn't extend behind the panel.
      const origWidth = orig.width;
      if (origWidth && (origWidth.includes('100') || origWidth.includes('vw'))) {
        // Had explicit inline width — replace with calc
        el.style.width = `calc(${origWidth} - ${this.currentPx}px)`;
      } else {
        // Check computed: if the element is full-width and has a stylesheet
        // rule like width:100%, setting right alone won't shrink it.
        // Use calc(100% - panel) as a safe fallback.
        const cs = getComputedStyle(el);
        const computedW = parseFloat(cs.width);
        if (computedW >= window.innerWidth * 0.8) {
          el.style.width = `calc(100% - ${this.currentPx}px)`;
        }
      }
    }
  }
}
